/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import { usePrevious } from "@react-hookz/web";
import { warnOnce } from "@smartsheet/lodestar-utils";
import { getClientId } from "@smartsheet/lodestar-utils";
import { useForkRef } from "@smartsheet/lodestar-utils";
import { useActive } from "@smartsheet/lodestar-utils";
import { useCombobox } from "downshift";
import {
    useState,
    useRef,
    InputHTMLAttributes,
    RefAttributes,
    useEffect,
    useCallback,
    ChangeEventHandler,
} from "react";
import { useCombinedThemeMode } from "../../../../util/theme/useCombinedThemeMode";
import { useHovered } from "../../../../util/useHovered";
import { useA11yStatusMessages } from "../../hooks/useA11yStatusMessages";
import { useAutoHighlightWithLoading } from "../../hooks/useAutoHighlightWithLoading";
import { useFocused } from "../../hooks/useFocused";
import { useHorizontalResize } from "../../hooks/useHorizontalResize";
import { useSelectFlyout } from "../../hooks/useSelectFlyout";
import { highlightFirstAutomaticallyStateReducer } from "../../reducers/highlightFirstAutomaticallyStateReducer";
import { BaseOptionType } from "../../types";
import {
    isCreatableMarkerOption,
    creatableMarkerOption,
    stopPropagationHandler,
    useSelectIds,
    isSelectV2OptionSeparator,
    findNextValidOptionIndex,
    squashSeparators,
} from "../../utils";
import { selectBasicFontTheme } from "../selectV2Theme";
import { Actions } from "../shared/Actions";
import { HiddenInput } from "../shared/HiddenInput";
import { Root } from "../shared/Root";
import { SelectClearButton } from "../shared/SelectClearButton";
import { SelectMenu } from "../shared/SelectMenu";
import { SelectPlaceholder } from "../shared/SelectPlaceholder";
import { ToggleButton } from "../shared/ToggleButton";
import { ComboBoxInput } from "./ComboBoxInput";
import { ContentBeforeContainer } from "./ContentBeforeContainer";
import { SelectedOptionFacade } from "./SelectedOptionFacade";
import { OptionalPropsNowRequired } from "./types";

export const PLACEHOLDER_MARGIN_DEFAULT = 12;
export const PLACEHOLDER_MARGIN_WITH_CONTENTBEFORE = 40;
interface SelectPropsBase<Option extends BaseOptionType> extends OptionalPropsNowRequired<Option, HTMLInputElement> {
    onChange: (value: Option | null) => void;
}

export const SelectComboBox = <Option extends BaseOptionType>({
    appearance = "outlined",
    "aria-describedby": ariaDescribedby,
    "aria-labelledby": ariaLabelledby,
    id,
    onChange,
    options,
    selectedOption,
    // optional below
    "aria-label": ariaLabel,
    className,
    clientId,
    formatOption,
    getHiddenInputValue,
    hiddenInputName,
    innerRef,
    isLoading,
    isOptionDisabled,
    isReadonly,
    isClearable,
    menuWidth = "match-control",
    maxMenuHeight,
    maxOptionsUntilVirtualization,
    menuId,
    menuPlacement = "bottom",
    menuPosition,
    onBlur: providedOnBlur,
    onCreateOption,
    onFocus: providedOnFocus,
    optionHeight,
    optionToString,
    portalProps,
    filterOption,
    getOptionKey,
    validationState,
    menuIsOpen,
    setMenuIsOpen,
    onMenuIsOpenChanged,
    inputValue: providedInputValue,
    setInputValue: providedSetInputValue,
    hideDropdownArrow = false,
    formatSelectedValue,
    placeholder,
    contentBefore,
    onKeyDown: providedOnKeyDown,
    highlightFirstOptionAutomatically = false,
    onPaste: providedOnPaste,
}: SelectPropsBase<Option>) => {
    const a11yMessages = useA11yStatusMessages();
    const themeMode = useCombinedThemeMode();
    const isCreatable = typeof onCreateOption === "function";
    const isInteractive = !isReadonly;
    const [inputValue, setInputValue] = useState(providedInputValue ?? "");
    const { isFocused, onBlur, onFocus } = useFocused({
        onFocus: providedOnFocus,
        onBlur: providedOnBlur,
    });
    const [filteredOptions, setFilteredOptions] = useState<Option[] | null>(null);
    const { ref: resizeRef, width: targetWidth } = useHorizontalResize<HTMLDivElement>();
    const localContainerRef = useRef<HTMLDivElement>(null);
    const containerRef = useForkRef(localContainerRef, resizeRef);
    const localInputRef = useRef<HTMLInputElement>(null);
    const forkedInputRef = useForkRef(localInputRef, innerRef || null);

    const {
        isHovered: isToggleHovered,
        onMouseEnter: onToggleMouseEnter,
        onMouseLeave: onToggleMouseLeave,
    } = useHovered<HTMLButtonElement>();

    const {
        isActive: isToggleActive,
        onMouseDown: onTAMouseDown,
        onMouseEnter: onTAMouseEnter,
        onMouseLeave: onTAMouseLeave,
    } = useActive({
        onMouseEnter: onToggleMouseEnter,
        onMouseLeave: onToggleMouseLeave,
    });
    const resetFiltering = () => {
        setInputValue("");
        providedSetInputValue?.("");
        setFilteredOptions(null);
    };

    const optionsToDisplay = filteredOptions || options;

    const doFilter = useCallback(
        (forInputValue: string) => {
            if (!forInputValue) {
                return;
            }
            const inputLowerCase = forInputValue?.trim().toLowerCase() ?? "";
            const filteredItems = options.filter((opt) => {
                if (isSelectV2OptionSeparator(opt)) {
                    return true;
                }
                return filterOption(opt, inputLowerCase);
            });
            const squashedAndFilteredItems = squashSeparators(filteredItems);
            if (isCreatable) {
                setFilteredOptions([creatableMarkerOption as unknown as Option, ...squashedAndFilteredItems]);
            } else {
                setFilteredOptions(squashedAndFilteredItems);
            }
        },
        [filterOption, isCreatable, options]
    );

    const {
        isOpen,
        closeMenu,
        getToggleButtonProps,
        getMenuProps,
        getInputProps,
        getComboboxProps,
        highlightedIndex,
        getItemProps,
        selectedItem,
        toggleMenu,
        setHighlightedIndex,
    } = useCombobox({
        ...a11yMessages,
        ...useSelectIds("combobox", id),
        ...(menuIsOpen !== undefined ? { isOpen: menuIsOpen } : null),
        circularNavigation: false,
        inputValue,
        items: optionsToDisplay,
        itemToString: (item) => (item === null ? "" : optionToString(item)),
        selectedItem: selectedOption,
        onSelectedItemChange: ({ selectedItem }) => onChange(selectedItem ?? null),
        onInputValueChange: ({ inputValue, isOpen }) => {
            // Don't try and filter when the menu is closed. This can be triggered by
            // the selection changing.
            if (isOpen && inputValue) {
                doFilter(inputValue);
            } else {
                resetFiltering();
            }
        },
        stateReducer: (currentState, actionAndChanges) => {
            const { type, changes } = actionAndChanges;

            switch (type) {
                case useCombobox.stateChangeTypes.InputKeyDownEscape:
                    // Cancel the escape key handling. We want to handle this key on our own
                    // through the StackOrderManager.
                    return currentState;
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                case useCombobox.stateChangeTypes.ItemClick:
                    if (changes.selectedItem) {
                        return isOptionDisabled?.(changes.selectedItem) ? currentState : changes;
                    }
            }

            if (changes.highlightedIndex) {
                switch (type) {
                    case useCombobox.stateChangeTypes.InputKeyDownArrowUp:
                    case useCombobox.stateChangeTypes.InputKeyDownArrowDown:
                        if (isSelectV2OptionSeparator(optionsToDisplay[changes.highlightedIndex])) {
                            return {
                                ...changes,
                                highlightedIndex: findNextValidOptionIndex(
                                    options,
                                    changes.highlightedIndex,
                                    type === useCombobox.stateChangeTypes.InputKeyDownArrowDown
                                ),
                            };
                        }
                }
            }

            if (highlightFirstOptionAutomatically) {
                return highlightFirstAutomaticallyStateReducer<Option>(
                    optionsToDisplay,
                    currentState,
                    actionAndChanges
                );
            }

            return changes;
        },
        onStateChange: ({ type, selectedItem }) => {
            switch (type) {
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                case useCombobox.stateChangeTypes.ItemClick:
                    if (selectedItem) {
                        if (isCreatable && isCreatableMarkerOption(selectedItem) && inputValue) {
                            onCreateOption(inputValue);
                        }
                    }
                    // For any selection, clear things out.
                    resetFiltering();
                    break;
                case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
                    // Clear filtering on initial state based on controlled props
                    resetFiltering();
                    break;
                default:
                    break;
            }
        },
        onIsOpenChange: ({ type, isOpen: openState }) => {
            if (process.env.NODE_ENV !== "production") {
                warnOnceForDeprecatedSetMenuIsOpenProp =
                    warnOnceForDeprecatedSetMenuIsOpenProp ??
                    warnOnce(
                        "SelectV2 (combobox): setMenuIsOpen is deprecated. Please use onMenuIsOpenChanged instead."
                    );
                if (setMenuIsOpen) {
                    warnOnceForDeprecatedSetMenuIsOpenProp();
                }
            }
            // setMenuIsOpen is deprecated. Use onMenuIsOpenChanged if it's available instead.
            (onMenuIsOpenChanged ?? setMenuIsOpen)?.(!!openState);

            switch (type) {
                case useCombobox.stateChangeTypes.InputBlur:
                    resetFiltering();
                    break;
            }
        },
    });

    const { flyoutProps, targetRef, ariaOwns, ariaControls } = useSelectFlyout({
        menuId,
        clientId,
        closeMenu,
        isOpen,
        targetRef: containerRef,
        menuPlacement,
        menuPosition,
        portalProps,
        updateKey: String(isOpen),
    });

    const downshiftInputProps = getInputProps({
        value: inputValue,
        onBlur,
        onFocus,
        "aria-describedby": ariaDescribedby,
        "aria-label": ariaLabel,
        "aria-labelledby": ariaLabelledby,
        onPaste: providedOnPaste,
        onKeyDown: (e) => {
            providedOnKeyDown?.(e, {
                highlightedIndex,
                // pass optionsToDisplay to match highlightedIndex
                options: optionsToDisplay,
            });
            switch (e.key) {
                case "Tab":
                    if (isOpen) {
                        e.preventDefault();
                        localInputRef.current?.focus();
                        closeMenu();
                        resetFiltering();
                    }
                    break;
                case "Escape":
                    resetFiltering();
            }
        },
        ref: forkedInputRef,
        "aria-controls": ariaControls,
        /**
         * inputValue has to be updated here due updates in different ticks as normal behavior.
         * Setting inputValue here avoids cursor resetting after input value modifications.
         */
        onChange: useCallback<ChangeEventHandler<HTMLInputElement>>(
            (e) => {
                const val = e.target.value;
                setInputValue(val);
                providedSetInputValue?.(val);
            },
            [providedSetInputValue]
        ),
    }) as InputHTMLAttributes<HTMLInputElement> & RefAttributes<HTMLInputElement>;
    if (!isInteractive && downshiftInputProps) {
        downshiftInputProps.onChange = undefined;
        downshiftInputProps.onKeyDown = undefined;
        downshiftInputProps.readOnly = true;
    }

    const isContentBeforeVisible = contentBefore != undefined && !inputValue;

    useEffect(() => {
        if (providedInputValue !== undefined && providedInputValue !== inputValue) {
            setInputValue(providedInputValue);
        }
    }, [inputValue, providedInputValue]);

    // If options changes, we'll have to re-run the filtering step and re-render.
    // This is usually for async-loaded options.
    const previousOptions = usePrevious(options);
    useEffect(() => {
        if (previousOptions !== options) {
            if (inputValue) {
                doFilter(inputValue);
            } else {
                setFilteredOptions(null);
            }
        }
    }, [doFilter, options, previousOptions, inputValue]);

    useAutoHighlightWithLoading<Option>(
        setHighlightedIndex,
        optionsToDisplay,
        isLoading,
        highlightFirstOptionAutomatically,
        isCreatable,
        inputValue
    );

    return (
        // TODO: use the Root component, or align the styles at least. Needs focus states!
        <Root
            data-client-id={getClientId(clientId, "-root")}
            className={className}
            isFocused={isFocused || isOpen}
            validationState={validationState || "rest"}
            isReadonly={isReadonly}
            themeMode={themeMode}
            appearance={appearance}
            {...getComboboxProps({
                ref: targetRef,
                "aria-owns": ariaOwns,
                onClick: (e) => {
                    e.stopPropagation();
                    if (isInteractive) {
                        if (!isOpen) {
                            localInputRef.current?.focus();
                        }
                        toggleMenu();
                    }
                },
            })}
        >
            <HiddenInput
                clientId={clientId}
                hiddenInputName={hiddenInputName}
                getHiddenInputValue={getHiddenInputValue}
                selectionType={selectedOption}
            />
            {isContentBeforeVisible && <ContentBeforeContainer>{contentBefore}</ContentBeforeContainer>}
            <ComboBoxInput
                data-client-id={getClientId(clientId, "-input")}
                readOnly={isReadonly}
                isReadonly={isReadonly}
                isContentBeforeVisible={isContentBeforeVisible}
                {...downshiftInputProps}
                id={id}
                aria-readonly={isReadonly}
                aria-labelledby={ariaLabelledby}
                themeMode={themeMode}
            />
            {selectedOption === null && !inputValue && placeholder && (
                <SelectPlaceholder hasContentBefore={!!contentBefore}>{placeholder}</SelectPlaceholder>
            )}
            {isInteractive && (
                <Actions isReadonly={isReadonly} themeMode={themeMode}>
                    <ToggleButton
                        {...getToggleButtonProps({
                            // Don't let the container handle this.
                            onClick: stopPropagationHandler,
                        })}
                        clientId={clientId}
                        isReadonly={isReadonly}
                        hideDropdownArrow={hideDropdownArrow}
                        isHovered={isToggleHovered}
                        isActive={isToggleActive}
                        isOpen={isOpen}
                        onMouseEnter={onTAMouseEnter}
                        onMouseLeave={onTAMouseLeave}
                        onMouseDown={onTAMouseDown}
                    />

                    {isClearable && selectedOption && (
                        <SelectClearButton
                            refocusTargetElement={() => {
                                localInputRef.current?.focus();
                            }}
                            resetFiltering={resetFiltering}
                            clearSelection={onChange}
                            isMultiSelect={false}
                        />
                    )}
                </Actions>
            )}
            {selectedItem &&
                !inputValue &&
                (formatSelectedValue !== undefined ? (
                    formatSelectedValue({ option: selectedOption })
                ) : (
                    <SelectedOptionFacade
                        {...selectBasicFontTheme.variant[themeMode]}
                        isContentBeforeVisible={isContentBeforeVisible}
                        themeMode={themeMode}
                        lineClamp={1}
                    >
                        {optionToString(selectedItem)}
                    </SelectedOptionFacade>
                ))}
            <SelectMenu<Option>
                allSelected={false}
                enableSelectAll={false}
                flyoutProps={flyoutProps}
                formatOption={formatOption}
                getItemProps={getItemProps}
                getMenuProps={getMenuProps}
                highlightedIndex={highlightedIndex}
                inputValue={inputValue}
                isLoading={isLoading}
                isOptionDisabled={isOptionDisabled}
                isOpen={isOpen}
                menuWidth={menuWidth}
                maxMenuHeight={maxMenuHeight}
                maxOptionsUntilVirtualization={maxOptionsUntilVirtualization}
                optionHeight={optionHeight}
                options={optionsToDisplay}
                optionToString={optionToString}
                selectedItems={[]}
                selectedOption={selectedOption}
                targetWidth={targetWidth}
                getOptionKey={getOptionKey}
            />
        </Root>
    );
};

let warnOnceForDeprecatedSetMenuIsOpenProp: undefined | (() => void);
